/**
 * Merge `metadata` from SearchContact (or similar) into a flat string-keyed map.
 * Supports plain objects; JSON strings are parsed best-effort.
 *
 * @param {unknown} metadata
 * @returns {Record<string, unknown>}
 */
export function flattenContactMetadata(metadata) {
  if (metadata == null) return {};
   if (typeof metadata === "string") {
    try {
      const parsed = JSON.parse(metadata);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return flattenContactMetadata(parsed);
      }
    } catch {
      return {};
    }
    return {};
  }
  if (typeof metadata === "object" && !Array.isArray(metadata)) {
    let flat = { ...metadata };
    for (let i = 0; i < 8; i += 1) {
      let changed = false;
      if (flat.metadata != null) {
        const inner = flattenContactMetadata(flat.metadata);
        const { metadata: _m, ...rest } = flat;
        flat = { ...rest, ...inner };
        changed = true;
      }
      if (flat.additional_attributes != null) {
        const inner = flattenAdditionalAttributes(flat.additional_attributes);
        const { additional_attributes: _a, ...rest } = flat;
        flat = { ...rest, ...inner };
        changed = true;
      }
      if (!changed) break;
    }
    return flat;
  }
  return {};
}

/**
 * Flatten `additional_attributes` when the API returns an array of single-key objects
 * (e.g. [{ address: "..." }, { city: "..." }]), a plain object, or a JSON string (same as metadata).
 * Unwraps nested `metadata` / `additional_attributes` envelopes one level at a time until stable.
 *
 * @param {unknown} additional
 * @returns {Record<string, unknown>}
 */
export function flattenAdditionalAttributes(additional) {
  if (additional == null) return {};
  if (typeof additional === "string") {
    try {
      const parsed = JSON.parse(additional);
      if (parsed && typeof parsed === "object") {
        return flattenAdditionalAttributes(parsed);
      }
    } catch {
      return {};
    }
    return {};
  }
  let result;
  if (Array.isArray(additional)) {
    result = additional.reduce((acc, item) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        for (const [k, v] of Object.entries(item)) {
          acc[k] = v;
        }
      }
      return acc;
    }, {});
  } else if (typeof additional === "object") {
    result = { ...additional };
  } else {
    return {};
  }

  for (let i = 0; i < 8; i += 1) {
    let changed = false;
    if (result.metadata != null) {
      const inner = flattenContactMetadata(result.metadata);
      const { metadata: _m, ...rest } = result;
      result = { ...rest, ...inner };
      changed = true;
    }
    if (result.additional_attributes != null) {
      const inner = flattenAdditionalAttributes(result.additional_attributes);
      const { additional_attributes: _a, ...rest } = result;
      result = { ...rest, ...inner };
      changed = true;
    }
    if (!changed) break;
  }

  return result;
}

/**
 * Merge message `contact_vars` with `searchContact` payload for the details panel (flat string-keyed map).
 * Strips and flattens `metadata` and `additional_attributes` on **both** sides so nested objects are not shown as one blob.
 *
 * @param {Record<string, unknown>|null|undefined} contactVars
 * @param {Record<string, unknown>|null|undefined} fetchedContact
 * @returns {Record<string, unknown>}
 */
export function mergeContactPanelAttributes(contactVars, fetchedContact) {
  const varsRaw = contactVars && typeof contactVars === "object" ? { ...contactVars } : {};
  const fetched = fetchedContact && typeof fetchedContact === "object" ? { ...fetchedContact } : {};

  const {
    metadata: varsMetadata,
    additional_attributes: varsAdditional,
    ...varsRest
  } = varsRaw;
  const varsMetaFlat = flattenContactMetadata(varsMetadata);
  const varsAdditionalFlat = flattenAdditionalAttributes(varsAdditional);

  const additionalFlat = flattenAdditionalAttributes(fetched.additional_attributes);
  const fetchedMetaFlat = flattenContactMetadata(fetched.metadata);
  const { additional_attributes: _omitA, metadata: _omitM, ...fetchedRest } = fetched;

  return {
    ...varsRest,
    ...varsAdditionalFlat,
    ...varsMetaFlat,
    ...additionalFlat,
    ...fetchedMetaFlat,
    ...fetchedRest,
  };
}
